import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import { MoneyProvider, useMoney } from "./lib/money";
import Layout from "./components/Layout";
import { Loading } from "./components/common";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";

// Pages load on demand: the first screen downloads only login, layout and dashboard
const Workers = lazy(() => import("./pages/Workers"));
const WorkerDetail = lazy(() => import("./pages/WorkerDetail"));
const Sites = lazy(() => import("./pages/Sites"));
const SiteDetail = lazy(() => import("./pages/SiteDetail"));
const Vehicles = lazy(() => import("./pages/Vehicles"));
const VehicleDetail = lazy(() => import("./pages/VehicleDetail"));
const Planning = lazy(() => import("./pages/Planning"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const Operations = lazy(() => import("./pages/Operations"));
const PrintSheet = lazy(() => import("./pages/PrintSheet"));
const PrintVehicles = lazy(() => import("./pages/PrintVehicles"));
const Finance = lazy(() => import("./pages/Finance"));
const PrintQuoteWork = lazy(() => import("./pages/PrintQuoteWork"));
const Documents = lazy(() => import("./pages/Documents"));
const Notifications = lazy(() => import("./pages/Notifications"));
const UsersPage = lazy(() => import("./pages/Users"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const MyShifts = lazy(() => import("./pages/MyShifts"));
const ImportPage = lazy(() => import("./pages/ImportPage"));

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

const A = ["admin"];
const S = ["admin", "responsabile"];
const O = ["operaio"];

function AppRoutes() {
  const { user } = useAuth();
  useMoney();
  return (
    <Suspense fallback={<Loading />}>
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/stampa-presenze" element={<Protected roles={S}><PrintSheet /></Protected>} />
      <Route path="/stampa-lavorazioni/:id" element={<Protected roles={A}><PrintQuoteWork /></Protected>} />
      <Route path="/stampa-mezzi" element={<Protected roles={A}><PrintVehicles /></Protected>} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="operai" element={<Protected roles={A}><Workers /></Protected>} />
        <Route path="operai/:id" element={<Protected roles={S}><WorkerDetail /></Protected>} />
        <Route path="cantieri" element={<Protected roles={S}><Sites /></Protected>} />
        <Route path="cantieri/:id" element={<Protected roles={S}><SiteDetail /></Protected>} />
        <Route path="mezzi" element={<Protected roles={A}><Vehicles /></Protected>} />
        <Route path="mezzi/:id" element={<Protected roles={S}><VehicleDetail /></Protected>} />
        <Route path="pianificazione" element={<Protected roles={S}><Planning /></Protected>} />
        <Route path="calendario" element={<CalendarPage />} />
        <Route path="operativa" element={<Protected roles={S}><Operations /></Protected>} />
        <Route path="economia" element={<Protected roles={A}><Finance /></Protected>} />
        <Route path="documenti" element={<Documents />} />
        <Route path="notifiche" element={<Notifications />} />
        <Route path="utenti" element={<Protected roles={A}><UsersPage /></Protected>} />
        <Route path="impostazioni" element={<Protected roles={A}><SettingsPage /></Protected>} />
        <Route path="importa" element={<Protected roles={A}><ImportPage /></Protected>} />
        <Route path="profilo" element={<Profile />} />
        <Route path="turni" element={<Protected roles={O}><MyShifts /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MoneyProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </MoneyProvider>
      <Toaster theme="dark" position="top-right" richColors closeButton />
    </AuthProvider>
  );
}
